import React, { useState, useMemo } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useRouter, useLocalSearchParams } from 'expo-router';
import {
  MessageCircle,
  Users,
  TrendingUp,
} from 'lucide-react-native';
import Animated, { FadeInUp, FadeIn } from 'react-native-reanimated';
import { track, useTrackScreen } from '@/lib/analytics';
import { useThemeColors } from '@/lib/useThemeColors';
import { SectionDivider } from '@/components/SectionDivider';
import { ScreenFrame } from '@/components/ScreenFrame';
import type {
  Conversation,
  Message,
} from '@/lib/state/messaging-store';
import { useProfileStore } from '@/lib/state/profile-store';
import { useSubscriptionStore } from '@/lib/state/subscription-store';
import { isWeb } from '@/lib/platform';
import { usePhoneShell } from '@/lib/useShell';
import { mode } from '@/lib/config/app-mode';
import { AppLayout } from '@/components/AppLayout';
import { RealtimeChat, READ_ONLY_SEND_COPY } from '@/components/RealtimeChat';
import { isRelationshipDenied } from '@/lib/supabase/messaging-mappers';
import { publicCardKeys } from '@/lib/company/public-card';
import { useQueryClient } from '@tanstack/react-query';
import { UpgradePrompt } from '@/components/subscription';
import { UsageIndicator } from '@/components/subscription/UsageIndicator';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useAlert } from '@/components/AlertDialog';
import { handleError, getUserMessage } from '@/lib/errors';
import { isObjectionableContentError } from '@/lib/moderation/api';
import {
  useRealtimeConversations,
  usePresence,
  useConversations,
  useMessages,
  useSendMessage,
  mapConversationRow,
  mapMessageRow,
  isSupabaseConfigured,
} from '@/lib/supabase';
import { formatConversationTime } from '@/lib/format';

// Mock conversations for demo
const mockConversations: Conversation[] = [
  {
    id: 'conv-1',
    startupId: 'startup-1',
    startupName: 'TechVenture AI',
    investorId: 'inv-1',
    investorProfileId: 'inv-1',
    investorName: 'Sarah Chen',
    // A read mark far in the future so sample mode demonstrates the RECEIPT
    // rather than sitting permanently on "Sent", which is also what a broken
    // receipt looks like. The counterparty side is the one that matters: an
    // investor reads startup_last_read_at, a founder reads investor_last_read_at.
    startupLastReadAt: '2099-01-01T00:00:00.000Z',
    investorLastReadAt: '2099-01-01T00:00:00.000Z',
    lastMessage: 'Looking forward to our call next week!',
    lastMessageTime: '2 hours ago',
    unreadCount: 2,
    status: 'active',
  },
  {
    id: 'conv-2',
    startupId: 'startup-1',
    startupName: 'TechVenture AI',
    investorId: 'inv-2',
    investorProfileId: 'inv-2',
    investorName: 'Michael Roberts',
    lastMessage: 'Can you share the updated financials?',
    lastMessageTime: 'Yesterday',
    unreadCount: 0,
    status: 'active',
  },
  {
    id: 'conv-3',
    startupId: 'startup-1',
    startupName: 'TechVenture AI',
    investorId: 'inv-3',
    investorProfileId: 'inv-3',
    investorName: 'Emily Wong',
    lastMessage: 'Thank you for the pitch deck.',
    lastMessageTime: '3 days ago',
    unreadCount: 0,
    status: 'active',
  },
];

const mockMessages: Message[] = [
  {
    id: 'msg-1',
    conversationId: 'conv-1',
    senderId: 'inv-1',
    senderName: 'Sarah Chen',
    senderType: 'investor',
    text: 'Hi! I reviewed your pitch deck and I have some questions about your go-to-market strategy.',
    timestamp: '10:30 AM',
  },
  {
    id: 'msg-2',
    conversationId: 'conv-1',
    senderId: 'startup-1',
    senderName: 'John (Founder)',
    senderType: 'startup',
    senderRole: 'founder',
    text: 'Thank you for your interest! Happy to discuss. We are focusing on enterprise customers initially.',
    timestamp: '10:45 AM',
  },
  {
    id: 'msg-3',
    conversationId: 'conv-1',
    senderId: 'inv-1',
    senderName: 'Sarah Chen',
    senderType: 'investor',
    text: 'That makes sense. What is your current pipeline looking like?',
    timestamp: '11:00 AM',
  },
  {
    id: 'msg-4',
    conversationId: 'conv-1',
    senderId: 'startup-1',
    senderName: 'Maria (IR)',
    senderType: 'startup',
    senderRole: 'investor_relations',
    text: 'We have 15 qualified leads in our pipeline with 3 in final negotiations.',
    timestamp: '11:15 AM',
  },
  {
    id: 'msg-5',
    conversationId: 'conv-1',
    senderId: 'inv-1',
    senderName: 'Sarah Chen',
    senderType: 'investor',
    text: 'Looking forward to our call next week!',
    timestamp: '2:00 PM',
  },
];

export default function StartupMessagesScreen() {
  useTrackScreen('StartupMessages');
  const router = useRouter();
  const colors = useThemeColors();
  const phoneShell = usePhoneShell();
  // THE OPEN CONVERSATION IS A ROUTE, NOT STATE.
  //
  // It used to be `useState<Conversation | null>(null)`, which gave the thread no
  // URL: a notification could not link to the conversation it was about, a web
  // refresh dropped you back to the list, and nothing automated could reach a
  // thread to verify what it rendered.
  //
  // `setParams` rather than `push`, deliberately. It makes the thread addressable
  // (the URL updates, so a deep link and a refresh both land in the right place)
  // WITHOUT adding a history entry or a second instance of a tab screen. The
  // trade-off is that browser/hardware back does not close the thread — which is
  // exactly today's behaviour, so this change adds addressability and regresses
  // nothing. Making back close the thread is a separate, navigator-shaped change
  // that wants device testing.
  const { conversation: openConversationId } = useLocalSearchParams<{ conversation?: string }>();
  const [isSending, setIsSending] = useState(false);
  // Conversations the founder has opened this session — used to optimistically
  // zero the unread badge before the realtime UPDATE / refetch lands (and to
  // keep the "mark read on open" UX in sample mode, which has no backend).
  const [locallyReadIds, setLocallyReadIds] = useState<Set<string>>(new Set());
  // Local echo of messages sent while in sample/demo mode (no backend to write to).
  const [localMessages, setLocalMessages] = useState<Message[]>([]);

  const startupProfile = useProfileStore((s) => s.startupProfile);
  const user = useProfileStore((s) => s.user);
  const { error: showError } = useAlert();
  const sendMessage = useSendMessage();
  const queryClient = useQueryClient();

  // Feature gating
  const hasMessaging = useSubscriptionStore((s) => s.hasFeature('direct_messaging'));

  // Real-time subscriptions
  useRealtimeConversations(startupProfile?.id, 'startup');

  // Real conversations (production) — snake→camel via mapConversationRow.
  const { data: conversationRows } = useConversations(startupProfile?.id, 'startup');
  const realConversations = useMemo(
    () => (conversationRows ?? []).map((row) => mapConversationRow(row)),
    [conversationRows]
  );

  // Real data in production; mock arrays ONLY behind mode.showSampleData.
  const displayConversations = useMemo(() => {
    const base = realConversations.length > 0
      ? realConversations
      : (mode.showSampleData ? mockConversations : []);
    // Optimistically clear the unread badge for conversations opened this session.
    return base.map((c) => (locallyReadIds.has(c.id) ? { ...c, unreadCount: 0 } : c));
  }, [realConversations, locallyReadIds]);


  // Derived, so the route is the single source of truth. An id that matches
  // nothing — a stale link, or a conversation this user cannot see — resolves to
  // null and shows the list rather than an empty thread.
  const selectedConversation = useMemo(
    () => displayConversations.find((c) => c.id === openConversationId) ?? null,
    [displayConversations, openConversationId]
  );

  // Real messages for the open conversation.
  const { data: messageRows } = useMessages(selectedConversation?.id);
  const realMessages = useMemo(
    () => (messageRows ?? []).map(mapMessageRow),
    [messageRows]
  );

  // Presence tracking for online indicators
  const { isUserOnline } = usePresence(
    startupProfile ? { id: startupProfile.id, name: startupProfile.name } : null,
    'global'
  );

  const conversationMessages = useMemo(() => {
    if (!selectedConversation) return [];
    if (realMessages.length > 0) return realMessages;
    const local = localMessages.filter((m) => m.conversationId === selectedConversation.id);
    if (mode.showSampleData) {
      const base = mockMessages.filter((m) => m.conversationId === selectedConversation.id);
      return [...base, ...local];
    }
    return local;
  }, [selectedConversation, realMessages, localMessages]);

  const totalUnread = displayConversations.reduce((sum, c) => sum + c.unreadCount, 0);

  // Show upgrade prompt if user doesn't have messaging access
  if (!hasMessaging) {
    return (
      <AppLayout title="Messages">
        <View className="flex-1 p-5" style={{ backgroundColor: colors.surface.secondary }}>
          <SafeAreaView edges={isWeb ? [] : ['top']} className="flex-1 justify-center">
            <View className="items-center mb-8">
              <View
                className="w-20 h-20 rounded-full items-center justify-center mb-4"
                style={{ backgroundColor: colors.primary[100] }}
              >
                <MessageCircle size={40} color={colors.primary[600]} />
              </View>
              <Text
                style={{ color: colors.text.primary }}
                className="text-2xl font-bold text-center mb-2"
              >
                Direct Messaging
              </Text>
              <Text
                style={{ color: colors.text.secondary }}
                className="text-center px-4"
              >
                Connect directly with interested investors and respond to their inquiries
              </Text>
            </View>
            <UpgradePrompt
              targetTier="standard"
              variant="fullscreen"
              headline="Unlock Investor Messaging"
              lockedTitle="Investor messaging"
              maxFeatures={4}
            />
          </SafeAreaView>
        </View>
      </AppLayout>
    );
  }

  const handleSelectConversation = (conv: Conversation) => {
    track('conversation_opened', { conversationId: conv.id });
    router.setParams({ conversation: conv.id });
    // Read-marking against the backend happens inside RealtimeChat
    // (markMessagesAsRead) for real conversations; here we just optimistically
    // zero the badge so the list reflects the open state immediately.
    if (conv.unreadCount > 0) {
      setLocallyReadIds((prev) => new Set(prev).add(conv.id));
    }
  };

  const handleSendMessage = async (text: string) => {
    if (!selectedConversation) return;

    setIsSending(true);
    try {
      if (mode.isMock || !isSupabaseConfigured()) {
        // Sample/demo mode: no backend — echo the message locally so the UX works.
        await new Promise((resolve) => setTimeout(resolve, 300));
        const localMsg: Message = {
          id: `msg-${Date.now()}`,
          conversationId: selectedConversation.id,
          senderId: user?.id ?? startupProfile?.id ?? 'startup-1',
          senderName: user?.name || startupProfile?.name || 'You',
          senderType: 'startup',
          senderRole: 'founder',
          text,
          // An ISO instant, like every server-written message — the bubble
          // formats it at render. This used to be pre-formatted here, which is
          // why `timestamp` carried two different shapes.
          timestamp: new Date().toISOString(),
        };
        setLocalMessages((prev) => [...prev, localMsg]);
      } else {
        // Production: write to Supabase. sender_id MUST be the auth uid — the
        // messages RLS INSERT policy checks sender_id = auth.uid().
        //
        // sender_role is NOT sent any more. It was hardcoded to 'founder' here,
        // which mislabelled every co-founder and every other team member in the
        // thread — and the INSERT policy constrains sender_id and nothing else,
        // so whatever this client claimed about its own identity was accepted
        // unverified. The messages_set_sender_identity trigger now derives
        // sender_name/sender_role/sender_type from server-known relationships,
        // and realtime reflects the corrected row straight back into the list.
        await sendMessage.mutateAsync({
          conversation_id: selectedConversation.id,
          sender_id: user?.id ?? '',
          sender_type: 'startup',
          text,
        });
      }
      track('message_sent', { conversationId: selectedConversation.id });
    } catch (err) {
      // The objectionable-text filter trigger is an expected, user-actionable
      // rejection (not a bug) — show its readable sentence directly instead of
      // routing it through handleError, which would both report it to Sentry
      // as a genuine error and fall back to the generic "unexpected error" copy.
      if (isObjectionableContentError(err)) {
        showError('Message Failed', getUserMessage(err));
      } else if (isRelationshipDenied(err)) {
        // A read-only thread (the company closed, owner decision #9) refuses
        // the insert with 42501 — expected, so not reported. Refreshing the
        // public card swaps the composer for the read-only notice.
        void queryClient.invalidateQueries({ queryKey: publicCardKeys.all });
        showError('Message Not Sent', READ_ONLY_SEND_COPY);
      } else {
        const { message, isCancelled } = handleError(err, {
          context: 'StartupMessages.handleSendMessage',
        });
        if (!isCancelled) showError('Message Failed', message);
      }
    } finally {
      setIsSending(false);
    }
  };

  // Chat View - use RealtimeChat component
  if (selectedConversation) {
    return (
      <AppLayout title="Messages" constrainWidth={false}>
        <RealtimeChat
          conversation={selectedConversation}
          messages={conversationMessages}
          currentUser={{
            // Auth uid — markMessagesAsRead / message sender_id are keyed on it.
            id: user?.id || startupProfile?.id || 'startup-1',
            name: startupProfile?.name || 'Startup',
            type: 'startup',
            role: 'founder',
          }}
          onBack={() => router.setParams({ conversation: undefined })}
          onSendMessage={handleSendMessage}
          isSending={isSending}
        />
      </AppLayout>
    );
  }

  // Conversation List View
  return (
    <AppLayout title="Messages">
      <View className="flex-1" style={{ backgroundColor: colors.surface.secondary }}>
        {/* Pinned, and carrying the global controls — see the note on score.tsx.
           The usage meter moved into the body: it is descriptive metadata, and a
           navigation row that also has to hold a growing counter is the row that
           squeezes the controls out. No bottomEdge: tab screen. */}
        <ScreenFrame
          eyebrow="Investor Relations"
          title="Messages"
          actions={
            <>
              <AnimatedPressable
                onPress={() => router.push('/startup-team')}
                accessibilityRole="button"
                accessibilityLabel="Team members"
                style={{
                  width: 44, height: 44, borderRadius: 22,
                  alignItems: 'center', justifyContent: 'center',
                  backgroundColor: colors.surface.primary,
                }}
              >
                <Users size={22} color={colors.text.secondary} />
              </AnimatedPressable>
              <ScreenFrame.GlobalActions />
            </>
          }
        >
          <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
            {phoneShell && (
              <View className="px-5 pb-2">
                <UsageIndicator feature="direct_messaging" suffix="/mo" />
              </View>
            )}
            {/* Stats */}
            <Animated.View
              entering={FadeInUp.delay(200).duration(500)}
              className="px-5 mb-6"
            >
              <View className="flex-row gap-3">
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.06,
                    shadowRadius: 8,
                    elevation: 3,
                  }}
                >
                  <MessageCircle size={20} color={colors.primary[600]} />
                  <Text style={{ color: colors.text.primary }} className="text-2xl font-bold mt-2">
                    {displayConversations.length}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Conversations</Text>
                </View>
                <View
                  className="flex-1 rounded-2xl p-4"
                  style={{
                    backgroundColor: totalUnread > 0 ? colors.primary[50] : colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.06,
                    shadowRadius: 8,
                    elevation: 3,
                  }}
                >
                  <TrendingUp size={20} color={totalUnread > 0 ? colors.primary[600] : colors.text.tertiary} />
                  <Text
                    className="text-2xl font-bold mt-2"
                    style={{ color: totalUnread > 0 ? colors.primary[600] : colors.text.primary }}
                  >
                    {totalUnread}
                  </Text>
                  <Text style={{ color: colors.text.tertiary }} className="text-xs">Unread</Text>
                </View>
              </View>
            </Animated.View>

            {/* Conversations List */}
            <Animated.View
              entering={FadeInUp.delay(300).duration(500)}
              className="px-5 pb-8"
            >
              <SectionDivider label="Investor Conversations" marginTop={0} marginBottom={16} />
              {displayConversations.length > 0 ? (
                <View className="gap-3">
                  {displayConversations.map((conv, index) => (
                    <Animated.View
                      key={conv.id}
                      entering={FadeIn.delay(300 + index * 50).duration(300)}
                    >
                      <AnimatedPressable
                        onPress={() => handleSelectConversation(conv)}
                        className="rounded-2xl overflow-hidden"
                        style={{
                          backgroundColor: colors.surface.primary,
                          shadowColor: '#000',
                          shadowOffset: { width: 0, height: 1 },
                          shadowOpacity: 0.05,
                          shadowRadius: 3,
                          elevation: 2,
                        }}
                      >
                        <View className="p-4">
                          <View className="flex-row items-center gap-3">
                            {/* Avatar with online indicator */}
                            <View className="relative">
                              <View
                                className="w-12 h-12 rounded-full items-center justify-center"
                                style={{ backgroundColor: colors.primary[100] }}
                              >
                                <Text style={{ color: colors.primary[600] }} className="text-lg font-bold">
                                  {conv.investorName.charAt(0)}
                                </Text>
                              </View>
                              {/* Online indicator */}
                              <View
                                className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full border-2"
                                style={{
                                  backgroundColor: conv.investorId !== null && isUserOnline(conv.investorId)
                                    ? colors.traffic.green
                                    : colors.text.tertiary,
                                  borderColor: colors.surface.primary,
                                }}
                              />
                            </View>
                            <View className="flex-1">
                              <View className="flex-row items-center justify-between" style={{ gap: 8 }}>
                                <View className="flex-row items-center gap-2 flex-1">
                                  <Text style={{ color: colors.text.primary }} className="font-semibold flex-1" numberOfLines={1}>{conv.investorName}</Text>
                                </View>
                                <Text style={{ color: colors.text.tertiary }} className="text-xs shrink-0" numberOfLines={1}>
                                  {formatConversationTime(conv.lastMessageTime)}
                                </Text>
                              </View>
                              <View className="flex-row items-center justify-between mt-1">
                                <Text
                                  style={{ color: conv.unreadCount > 0 ? colors.text.secondary : colors.text.tertiary }}
                                  className="text-sm flex-1"
                                  numberOfLines={1}
                                >
                                  {conv.lastMessage}
                                </Text>
                                {conv.unreadCount > 0 && (
                                  <View
                                    className="w-6 h-6 rounded-full items-center justify-center ml-2"
                                    style={{ backgroundColor: colors.primary[600] }}
                                  >
                                    <Text style={{ color: colors.text.inverse }} className="text-xs font-bold">
                                      {conv.unreadCount}
                                    </Text>
                                  </View>
                                )}
                              </View>
                            </View>
                          </View>
                        </View>
                      </AnimatedPressable>
                    </Animated.View>
                  ))}
                </View>
              ) : (
                <View
                  className="rounded-2xl p-8 items-center"
                  style={{
                    backgroundColor: colors.surface.primary,
                    shadowColor: '#000',
                    shadowOffset: { width: 0, height: 2 },
                    shadowOpacity: 0.05,
                    shadowRadius: 10,
                    elevation: 2,
                  }}
                >
                  <MessageCircle size={48} color={colors.text.tertiary} />
                  <Text style={{ color: colors.text.secondary }} className="text-center mt-4">
                    No conversations yet.{'\n'}Investors will reach out when interested.
                  </Text>
                </View>
              )}
            </Animated.View>

            {/* Team Access Note */}
            <Animated.View
              entering={FadeInUp.delay(400).duration(500)}
              className="px-5 pb-8"
            >
              <View
                className="rounded-2xl p-4"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderWidth: 1,
                  borderColor: colors.border.light,
                }}
              >
                <View className="flex-row items-center gap-2 mb-2">
                  <Users size={16} color={colors.text.secondary} />
                  <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                    Team Access
                  </Text>
                </View>
                <Text style={{ color: colors.text.secondary }} className="text-xs">
                  All team members can view and respond to investor messages.
                  Messages show who sent them (Founder, Co-Founder, IR, etc.)
                </Text>
                <Pressable
                  onPress={() => router.push('/startup-team')}
                  className="mt-3"
                >
                  <Text style={{ color: colors.primary[600] }} className="text-sm font-medium">
                    Manage Team Members →
                  </Text>
                </Pressable>
              </View>
            </Animated.View>
          </ScrollView>
        </ScreenFrame>
      </View>
    </AppLayout>
  );
}
